import type { EntityDefinitions, EntityPropertyDefinitions } from '@/types/eds/Definitions';
import type {
  RawDefinitionsCollection,
  RawEntityDefinition,
  RawEntityPropertyDefinition,
} from '@/types/eds/RawDefinitions';
import type { EDSV2OverrideRenderDefinitionResponse, EDSV2Response } from '@/types/eds/api/responses/EDSV2Response';
import { NormalizedV2Property } from '@/types/eds/api/responses/NormalizedV2Response';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import { EdsDynamicOverrideMode } from '@/features/eds/enums/EdsDynamicOverrideMode';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { edsToCodeFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import HttpService from '@/services/HttpService/HttpService';
import MergeService from '@/services/MergeService/MergeService';
import RecordService from '@/services/RecordService/RecordService';
import TypeService from '@/services/TypeService/TypeService';

const DEFAULT_VALUE = '';
const EDS_DEFINITION_CACHE_TTL = 10 * 1000;

export class EdsRawDefinitionsService {
  private static inFlightRequests: Map<string, Promise<RawEntityDefinition | null>> = new Map();

  public static async requestRawEntityDefinition(
    entityCode: EdsEntityCodes,
    section?: string,
  ): Promise<RawEntityDefinition | null> {
    const key = `${entityCode}::${section ?? ''}`;

    if (this.inFlightRequests.has(key)) {
      return this.inFlightRequests.get(key)!;
    }

    const promise = (async () => {
      try {
        const { data } = await HttpService.get<HttpServiceResponse<RawDefinitionsCollection>>(
          `${import.meta.env.VITE_DEFINITIONS_API_TARGET}/definitions/render`,
          {
            nameFilter: entityCode,
            section,
          },
          {
            httpServiceDeduplicationTTL: EDS_DEFINITION_CACHE_TTL,
            baseURL: '/',
          },
        );
        return data?.[0];
      } catch (error) {
        console.error(error);
        return null;
      } finally {
        this.inFlightRequests.delete(key);
      }
    })();
    this.inFlightRequests.set(key, promise);

    return promise;
  }

  public static convertRawEntityDefinition(rawDefinition: RawEntityDefinition): EntityDefinitions {
    const fields = rawDefinition?.properties?.reduce(
      (propertiesRecord, rawProperty) => ({
        ...propertiesRecord,
        [edsToCodeFormat(rawProperty.propertyName)]:
          EdsRawDefinitionsService.convertRawEntityDefinitionProperty(rawProperty),
      }),
      {} as Record<string, EntityPropertyDefinitions>,
    );

    const entityName = rawDefinition.entityType.replace('_Entity', '');

    return {
      entityName,
      fieldGroup: null,
      sequenceNumber: rawDefinition.displayOrder,
      isDisplay: !rawDefinition.hidden,
      isEditable: !rawDefinition.isReadOnly,
      fields,
    };
  }

  public static injectOverrideRenderDefinition(
    response: EDSV2Response,
    rawDefinition: RawEntityDefinition,
    overrideMode?: EdsDynamicOverrideMode,
  ): RawEntityDefinition {
    overrideMode = overrideMode || EdsDynamicOverrideMode.Replace;
    const rawDefinitionClone = structuredClone(rawDefinition);
    const overrideRenderDefinitionProperties = response?.overrideRenderDefinition?.properties || [];

    const overridePropertiesRecord = overrideRenderDefinitionProperties.reduce(
      (acc, property) => {
        const { propertyName, ...rewriteData } = property;
        return {
          ...acc,
          [edsToCodeFormat(property.propertyName)]: rewriteData,
        };
      },
      {} as Record<string, Partial<Omit<RawEntityPropertyDefinition, 'propertyName'>>>,
    );

    if (overrideRenderDefinitionProperties) {
      rawDefinitionClone.properties = rawDefinitionClone.properties.reduce((acc, property) => {
        const propertyName = edsToCodeFormat(property.propertyName);
        const overridePropertyData = overridePropertiesRecord[propertyName];

        if (!(propertyName in overridePropertiesRecord) || !overridePropertyData) return [...acc, property];

        const cleanedUpOverride = RecordService.cleanRecordFromSpecificValue(overridePropertyData, null);

        return [
          ...acc,
          overrideMode === EdsDynamicOverrideMode.Replace
            ? { ...property, ...cleanedUpOverride }
            : MergeService.deepMergeObjects(property, cleanedUpOverride),
        ];
      }, [] as RawEntityPropertyDefinition[]);
    }

    return rawDefinitionClone;
  }

  public static normalizePartialOverrideRenderDefinitionProperties(
    overrideRenderDefinitionProperties: EDSV2OverrideRenderDefinitionResponse['properties'],
  ): Partial<NormalizedV2Property>[] {
    return overrideRenderDefinitionProperties.reduce((acc, property) => {
      const normalizedOverrideProperty = Object.keys(property).reduce((acc, key) => {
        return { ...acc, ...EdsRawDefinitionsService.adaptPropertySetting(key, property) };
      }, {} as Partial<NormalizedV2Property>);
      const cleanedNormalizedOverrideProperty = RecordService.cleanRecordFromSpecificValue(
        normalizedOverrideProperty,
        null,
      );

      return [...acc, cleanedNormalizedOverrideProperty];
    }, [] as Partial<NormalizedV2Property>[]);
  }

  public static convertRawEntityDefinitionProperty(
    rawPropertyDefinition: RawEntityPropertyDefinition,
  ): EntityPropertyDefinitions {
    return {
      value: DEFAULT_VALUE,
      displayValue: '',

      ...this.adaptPropertySetting('propertyName', rawPropertyDefinition),
      ...this.adaptPropertySetting('fieldGroup', rawPropertyDefinition),
      ...this.adaptPropertySetting('category', rawPropertyDefinition),
      ...this.adaptPropertySetting('uiControlType', rawPropertyDefinition),
      ...this.adaptPropertySetting('dataType', rawPropertyDefinition),
      ...this.adaptPropertySetting('label', rawPropertyDefinition),
      ...this.adaptPropertySetting('displayOrder', rawPropertyDefinition, 0),
      ...this.adaptPropertySetting('tooltip', rawPropertyDefinition),

      ...this.adaptPropertySetting('isSortable', rawPropertyDefinition, false),
      ...this.adaptPropertySetting('isFilterable', rawPropertyDefinition, false),
      ...this.adaptPropertySetting('isReadOnly', rawPropertyDefinition, false),
      ...this.adaptPropertySetting('isNullable', rawPropertyDefinition, false),
      ...this.adaptPropertySetting('hidden', rawPropertyDefinition, false),

      ...this.adaptPropertySetting('validation', rawPropertyDefinition, { allowedValues: [] }),
      ...this.adaptPropertySetting('metadataV2', rawPropertyDefinition, {}),

      ...this.adaptPropertySetting('filterConfiguration', rawPropertyDefinition, {}),
      ...this.adaptPropertySetting('sortingConfiguration', rawPropertyDefinition, {}),
      ...this.adaptPropertySetting('relationshipDefinition', rawPropertyDefinition),
    } as any as EntityPropertyDefinitions;
  }

  public static adaptPropertySetting(
    rawKey: string,
    rawProperty: Partial<RawEntityPropertyDefinition>,
    defaultValue?: any,
  ): Record<string, any> {
    const mapper = {
      dataType: 'dataType',
      propertyName: 'fieldName',
      fieldGroup: 'fieldGroup',
      category: 'category',
      uiControlType: 'fieldType',
      label: 'displayTitle',
      displayOrder: 'sequenceNumber',
      tooltip: 'tooltip',

      isSortable: 'isSortable',
      isFilterable: 'isFilterable',
      isReadOnly: 'isEditable',
      isNullable: 'isNullable',
      hidden: 'isDisplay',

      validation: 'validation',
      metadataV2: 'metadata',

      filterConfiguration: 'filterConfiguration',
      sortingConfiguration: 'sortingConfiguration',
      relationshipDefinition: 'relationshipDefinition',
    };

    if (rawKey in mapper) {
      let value = rawProperty?.[rawKey];

      if (rawKey === 'isSortable' && TypeService.isBoolean(rawProperty?.isSortable)) value = !!rawProperty?.isSortable;
      if (rawKey === 'isFilterable' && TypeService.isBoolean(rawProperty?.isFilterable))
        value = !!rawProperty?.isFilterable;
      if (rawKey === 'isReadOnly' && TypeService.isBoolean(rawProperty?.isReadOnly)) value = !rawProperty?.isReadOnly;
      if (rawKey === 'isNullable' && TypeService.isBoolean(rawProperty?.isNullable)) value = !!rawProperty?.isNullable;
      if (rawKey === 'hidden' && TypeService.isBoolean(rawProperty?.hidden)) value = !rawProperty?.hidden;
      if (rawKey === 'dataType') value = rawProperty?.dataType?.replace('_Entity', '');
      if (rawKey === 'validation' && Boolean(rawProperty.isNullable) && rawProperty.validation) {
        value = {
          ...rawProperty.validation,
          allowedValues: [{ label: '-', value: null }, ...(rawProperty?.validation?.allowedValues || [])],
        };
      }
      if (rawKey === 'fieldGroup') {
        if (value === '' || value === undefined) value = null;
      }
      if (rawKey === 'metadataV2') value = rawProperty?.metadataV2;

      if (value === undefined && defaultValue === undefined) return {} as Record<string, any>;
      if (value === undefined) value = defaultValue || null;

      return {
        [mapper[rawKey]]: value,
      };
    }

    return {} as Record<string, any>;
  }
}
